import React from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';

function ClauseRow({ c, idx, onChange, disabled }) {
  const [open, setOpen] = React.useState(false);
  const set = (patch) => onChange(idx, { ...c, ...patch });
  return (
    <div className={`rounded-lg border ${c.selected ? 'border-slate-200 bg-white' : 'border-dashed border-slate-200 bg-slate-50/60'}`} data-testid={`clause-row-${c.key || idx}`}>
      <div className="flex items-start gap-3 px-3 py-2">
        <input type="checkbox" checked={!!c.selected} disabled={disabled} onChange={(e) => set({ selected: e.target.checked })}
          className="w-4 h-4 mt-0.5 accent-fuchsia-600" data-testid={`clause-check-${c.key || idx}`} />
        <button type="button" onClick={() => setOpen(!open)} className="flex-1 min-w-0 text-left" data-testid={`clause-expand-${c.key || idx}`}>
          <p className={`text-xs leading-relaxed ${c.selected ? 'text-slate-700' : 'text-slate-400'} ${open ? '' : 'line-clamp-2'}`}>
            {c.title && <b className="text-slate-800">{c.title}. </b>}{c.body}
          </p>
        </button>
        <ChevronDown className={`w-4 h-4 mt-0.5 text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>
      {open && !disabled && (
        <div className="px-3 pb-3 space-y-2">
          <Input value={c.title || ''} onChange={(e) => set({ title: e.target.value })} placeholder="Heading (optional)" data-testid={`clause-title-${c.key || idx}`} />
          <Textarea value={c.body} rows={5} onChange={(e) => set({ body: e.target.value })} className="text-xs font-mono" data-testid={`clause-body-${c.key || idx}`} />
          {c.key && <p className="text-[11px] text-slate-400">Reference key: <code>{`{{ref:${c.key}}}`}</code></p>}
        </div>
      )}
    </div>
  );
}

export default function ClausesChecklist({ clauses = [], sections = [], onChange, disabled }) {
  const update = (i, c) => onChange(clauses.map((x, j) => (j === i ? c : x)));
  const addTo = (sectionKey) => onChange([...clauses, { key: `custom_${Date.now()}`, section_key: sectionKey, title: '', body: 'New clause text', selected: true }]);
  return (
    <div className="space-y-5" data-testid="clauses-checklist">
      <p className="text-xs text-slate-500">Untick clauses to leave them out. Numbering and clause cross-references update automatically.</p>
      {sections.map((s) => {
        const rows = clauses.map((c, i) => ({ c, i })).filter(({ c }) => c.section_key === s.key);
        return (
          <div key={s.key} data-testid={`clause-section-${s.key}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold text-slate-800">{s.name}</p>
              <span className="text-[11px] text-slate-400">{rows.filter((r) => r.c.selected).length}/{rows.length}</span>
            </div>
            <div className="space-y-1.5">
              {rows.map(({ c, i }) => <ClauseRow key={c.key || i} c={c} idx={i} onChange={update} disabled={disabled} />)}
            </div>
            {!disabled && (
              <button type="button" onClick={() => addTo(s.key)} className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-fuchsia-700" data-testid={`clause-add-${s.key}`}>
                <Plus className="w-3.5 h-3.5" /> Add clause
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
